import React from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  Users,
  GraduationCap,
  Trophy,
  Hotel,
  HeartHandshake,
  Sparkles,
  Stethoscope,
  Megaphone,
  ArrowRight,
} from 'lucide-react';
import { SectionHeading, FeatureCard, ImageBlock } from '@/components/Pieces';
import ContactForm from '@/components/ContactForm';
import FAQ from '@/components/FAQ';
import WorkforceROICalculator from '@/components/WorkforceROICalculator';
import usePageMeta from '@/hooks/usePageMeta';
import { img } from '@/lib/site';

const CATEGORIES = [
  { icon: Building2, title: 'Corporate Partnerships', desc: 'Workforce wellness, engagement, and culture-as-KPI programs for enterprise teams.' },
  { icon: Users, title: 'HR & PEO Partnerships', desc: 'Plug-and-play workforce performance modules for HR teams and PEOs.' },
  { icon: GraduationCap, title: 'School Partnerships', desc: 'Athletic, leadership, and academic ecosystems for K-12 districts.' },
  { icon: Trophy, title: 'Sports Organization Partnerships', desc: 'Performance, recovery, and mental resilience infrastructure for clubs.' },
  { icon: Hotel, title: 'Hospitality & Retreat Partnerships', desc: 'Resort-integrated team experiences and corporate retreats.' },
  { icon: HeartHandshake, title: 'Community Partnerships', desc: 'Youth, family, and workforce alignment with civic organizations.' },
  { icon: Sparkles, title: 'Wellness Collaborations', desc: 'Co-branded wellness programs that bolt onto existing benefits.' },
  { icon: Stethoscope, title: 'Healthcare Collaborations', desc: 'Workforce durability with healthcare networks and clinics.' },
  { icon: Megaphone, title: 'Sponsorship Opportunities', desc: 'Brand visibility across leagues, academy programs, and events.' },
];

export default function PartnershipPage() {
  usePageMeta({
    title: 'Partner With Ball Raw Performance | Corporate, Retreat, School & Community Partnerships',
    description:
      'Partner with Ball Raw Performance to deliver workforce performance, employee engagement, and corporate experience programs. Corporate, HR & PEO, retreat, school, sports, community, wellness, and healthcare partnerships built to scale.',
    path: '/partnerships',
  });
  return (
    <div data-testid="page-partnerships">
      <section className="relative bg-[#0A0A0A] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={img('partnership_meeting')} alt="Partnership meeting" className="w-full h-full object-cover opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/20" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
          <p className="brp-overline brp-reveal">Partnership Opportunities</p>
          <h1 className="brp-reveal brp-delay-1 font-display uppercase font-bold text-5xl md:text-7xl lg:text-8xl leading-[0.92] mt-6 max-w-4xl">
            Partner With <span className="text-[#D4AF37]">Ball Raw Performance.</span>
          </h1>
          <p className="brp-reveal brp-delay-2 mt-8 text-lg md:text-xl text-gray-300 max-w-2xl">
            Ball Raw Performance integrates into existing workforce wellness and engagement ecosystems — scalable, measurable, and built for enterprise.
          </p>
          <div className="brp-reveal brp-delay-3 mt-10 flex flex-wrap gap-4">
            <a href="#contact" className="btn-brp-primary" data-testid="partnership-cta-become">
              Become a Partner <ArrowRight size={16} />
            </a>
            <a href="#contact" className="btn-brp-outline-light" data-testid="partnership-cta-info">
              Request Partnership Information
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <SectionHeading
            overline="Partnership Categories"
            title="Built To Plug In. Built To Scale."
            subtitle="Nine partnership tracks — engineered to integrate with existing HR, benefits, operations, sports, and community ecosystems."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {CATEGORIES.map((c, i) => (
              <FeatureCard key={c.title} icon={c.icon} title={c.title} desc={c.desc} accent testId={`partner-${i}`} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 bg-[#0A0A0A] text-white">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading
              light
              overline="Why Partner"
              title="A Performance Layer For Your Organization."
              subtitle="Ball Raw Performance is not another vendor — it is an operational performance layer. We slot into your workforce, athletic, or community stack and ship measurable outcomes."
            />
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { n: '01', t: 'Integrates With Existing HR & Wellness' },
              { n: '02', t: 'Quarterly KPI Reporting' },
              { n: '03', t: 'Privacy-First Data Governance' },
              { n: '04', t: 'Scales From Pilot to Enterprise' },
              { n: '05', t: 'Branded, Co-Marketed Programs' },
              { n: '06', t: 'Cross-Division Performance Engine' },
            ].map((b) => (
              <div key={b.n} className="border border-[#1f1f1f] p-6 hover:border-[#D4AF37] transition">
                <div className="text-[#D4AF37] font-display text-3xl font-bold">{b.n}</div>
                <div className="mt-3 font-display uppercase tracking-wide">{b.t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI CALCULATOR */}
      <section className="py-20 md:py-28 px-6 md:px-10 bg-white" id="roi-calculator">
        <div className="max-w-[1400px] mx-auto">
          <WorkforceROICalculator
            title="Show the Business Case for Workforce Performance"
            subtitle="Employers, HR partners, PEOs, benefits consultants, and strategic partners can use the calculator to estimate workforce cost exposure and model the financial value of measurable improvement."
            sourcePage="partnerships"
            testIdPrefix="partner-roi"
          />
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 bg-[#F9F9F9]">
        <div className="max-w-[1400px] mx-auto">
          <FAQ
            testIdPrefix="partnership-faq"
            overline="Frequently Asked"
            title="Frequently Asked Questions"
            items={[
              {
                q: 'What types of organizations does Ball Raw Performance partner with?',
                a: 'We partner with employers, PEOs, HR organizations, retreat and event companies, municipalities, schools, nonprofits, and other organizations that value workforce performance and community impact.',
              },
              {
                q: 'How does a partnership with Ball Raw Performance work?',
                a: 'Every partnership begins with a conversation about shared goals and opportunities. Together, we develop a collaborative plan that creates value for both organizations.',
              },
              {
                q: 'How do partnerships with Ball Raw Performance create value?',
                a: 'Our partnerships help organizations expand services, improve client experiences, and create new opportunities for growth. We focus on building long term relationships that benefit everyone involved.',
              },
              {
                q: 'What partnership opportunities are available?',
                a: 'Partnerships may include referrals, strategic collaborations, event support, workforce performance programming, or customized initiatives. Every opportunity is based on shared goals and organizational alignment.',
              },
              {
                q: 'Why do organizations choose Ball Raw Performance?',
                a: 'Organizations choose Ball Raw Performance because we deliver customized workforce performance experiences that strengthen employee engagement, workplace culture, and long term business relationships.',
              },
              {
                q: 'What does the partnership process look like?',
                a: 'The process begins with a discovery meeting followed by planning and implementation. We work closely with every partner to build a successful long term relationship.',
              },
            ]}
          />
        </div>
      </section>

      <section id="contact" className="py-20 md:py-28 px-6 md:px-10 bg-white scroll-mt-24">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <SectionHeading
              overline="Speak With Ball Raw Performance"
              title="Tell Us About Your Workforce."
              subtitle="A partner specialist will follow up within one business day with a tailored next step."
            />
            <div className="mt-10 space-y-6">
              <div>
                <div className="brp-overline">Email</div>
                <a href="mailto:Support@ballraw.com" className="block mt-2 font-display text-2xl uppercase tracking-wide hover:text-[#D4AF37] transition">
                  Support@ballraw.com
                </a>
              </div>
              <div>
                <div className="brp-overline">Phone</div>
                <a href="tel:4803093608" className="block mt-2 font-display text-2xl uppercase tracking-wide hover:text-[#D4AF37] transition">
                  (480) 309-3608
                </a>
              </div>
              <div className="border-l-4 border-[#D4AF37] pl-5 mt-8">
                <p className="text-[#555] italic">
                  "Ball Raw Performance integrates into existing workforce wellness and engagement ecosystems."
                </p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <ContactForm
              formType="partnership"
              variant="dark"
              title="Request Partnership Information"
              subtitle="Tell us about your organization and partnership track of interest."
              showRole
              showEmployees
              showIndustry
              showInterest
              ctaLabel="Become a Partner"
              testIdPrefix="partnership-contact"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
